import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, FileText } from 'lucide-react';
import api from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';
import { Card } from '@/components/ui/card';
import { cn } from '@/lib/utils';

const STATUS_STYLES: Record<string, { label: string; className: string }> = {
  pending: {
    label: 'Pending',
    className: 'bg-zinc-100 text-zinc-700 dark:bg-zinc-800 dark:text-zinc-300',
  },
  reviewing: {
    label: 'AI reviewing',
    className: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300',
  },
  human_review: {
    label: 'Awaiting human reviewer',
    className: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300',
  },
  building: {
    label: 'Building',
    className: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300',
  },
  published: {
    label: 'Published',
    className: 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300',
  },
  rejected: {
    label: 'Rejected',
    className: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300',
  },
};

function StatusBadge({ status }: { status: string }) {
  const style = STATUS_STYLES[status] ?? {
    label: status,
    className: 'bg-zinc-100 text-zinc-700 dark:bg-zinc-800 dark:text-zinc-300',
  };
  return (
    <span
      className={cn(
        'inline-flex items-center px-2 py-0.5 rounded text-xs font-medium',
        style.className,
      )}
    >
      {style.label}
    </span>
  );
}

export function PluginSubmissions() {
  const { data, isLoading, error } = useQuery({
    queryKey: queryKeys.submissions.myList(),
    queryFn: () => api.listPluginSubmissions({ limit: 50 }),
  });

  return (
    <div className="max-w-5xl mx-auto p-8">
      <div className="mb-6">
        <Link
          to="/dev-console"
          className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Dev Console
        </Link>
      </div>

      <div className="mb-8">
        <h1 className="text-3xl font-bold text-foreground">My Plugin Submissions</h1>
        <p className="mt-2 text-muted-foreground">
          Track the review and build status of every arche you've submitted.
        </p>
      </div>

      {isLoading && <p className="text-muted-foreground">Loading...</p>}
      {error && (
        <p className="text-destructive">Failed to load submissions: {(error as Error).message}</p>
      )}

      {data && data.submissions.length === 0 && (
        <Card className="p-8 text-center">
          <FileText className="h-10 w-10 text-muted-foreground/50 mx-auto mb-3" />
          <p className="text-muted-foreground">You haven't submitted any plugins yet.</p>
        </Card>
      )}

      {data && data.submissions.length > 0 && (
        <Card className="overflow-hidden">
          <table className="w-full">
            <thead className="bg-muted/50 text-left text-sm">
              <tr>
                <th className="px-4 py-3 font-medium">Arche</th>
                <th className="px-4 py-3 font-medium">Version</th>
                <th className="px-4 py-3 font-medium">Status</th>
                <th className="px-4 py-3 font-medium">Submitted</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border text-sm">
              {data.submissions.map((s) => (
                <tr key={s.submissionId} className="hover:bg-muted/30">
                  <td className="px-4 py-3">
                    <Link
                      to={`/dev-console/submissions/${s.submissionId}`}
                      className="font-medium text-foreground hover:underline"
                    >
                      {s.manifestName}
                    </Link>
                    <div className="text-xs text-muted-foreground font-mono mt-0.5">
                      {s.submissionId}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-muted-foreground">{s.manifestVersion}</td>
                  <td className="px-4 py-3">
                    <StatusBadge status={s.status} />
                  </td>
                  <td className="px-4 py-3 text-muted-foreground">
                    {new Date(s.createdAt).toLocaleString()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}
    </div>
  );
}
